let recognition: SpeechRecognition | null = null

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionError {
  error: string
  message: string
}

export const speechToText = (
  language: string = 'en-US',
  onInterim: (transcript: string) => void,
  onFinal: (transcript: string) => void,
  onError: (error: Error) => void
) => {
  // Check for browser support
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
  
  if (!SpeechRecognition) {
    onError(new Error('Speech recognition not supported in this browser'))
    return
  }

  // Stop any existing recognition
  if (recognition) {
    recognition.stop()
  }

  recognition = new SpeechRecognition()
  
  // Configure recognition
  recognition.continuous = true
  recognition.interimResults = true
  recognition.lang = language
  recognition.maxAlternatives = 1

  // Handle results
  recognition.onresult = (event: SpeechRecognitionEvent) => {
    let interimTranscript = ''
    let finalTranscript = ''

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript
      
      if (event.results[i].isFinal) {
        finalTranscript += transcript
      } else {
        interimTranscript += transcript
      }
    }

    if (interimTranscript) {
      onInterim(interimTranscript)
    }
    
    if (finalTranscript) {
      onFinal(finalTranscript)
    }
  }

  // Handle errors
  recognition.onerror = (event: SpeechRecognitionError) => {
    let errorMessage = 'Speech recognition error'
    
    switch (event.error) {
      case 'no-speech':
        errorMessage = 'No speech detected. Please try again.'
        break
      case 'audio-capture':
        errorMessage = 'Microphone not accessible. Please check permissions.'
        break
      case 'not-allowed':
        errorMessage = 'Microphone permission denied. Please allow microphone access.'
        break
      case 'network':
        errorMessage = 'Network error. Please check your connection.'
        break
      case 'language-not-supported':
        errorMessage = 'Language not supported. Please try a different language.'
        break
      default:
        errorMessage = event.message || 'Unknown speech recognition error'
    }
    
    onError(new Error(errorMessage))
  }

  // Handle end of recognition
  recognition.onend = () => {
    recognition = null
  }

  // Start recognition
  try {
    recognition.start()
  } catch (error) {
    onError(new Error('Failed to start speech recognition'))
  }
}

export const stopListening = () => {
  if (recognition) {
    recognition.stop()
    recognition = null
  }
}

// Check if speech recognition is supported
export const isSpeechRecognitionSupported = (): boolean => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
}

// Get supported languages (this is a subset of commonly supported languages)
export const getSupportedLanguages = (): { code: string; name: string }[] => {
  return [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'fr-FR', name: 'French (France)' },
    { code: 'de-DE', name: 'German (Germany)' },
    { code: 'it-IT', name: 'Italian (Italy)' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'pt-PT', name: 'Portuguese (Portugal)' },
    { code: 'ru-RU', name: 'Russian (Russia)' },
    { code: 'ja-JP', name: 'Japanese (Japan)' },
    { code: 'ko-KR', name: 'Korean (South Korea)' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'zh-TW', name: 'Chinese (Traditional)' },
    { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
    { code: 'hi-IN', name: 'Hindi (India)' },
    { code: 'th-TH', name: 'Thai (Thailand)' },
    { code: 'vi-VN', name: 'Vietnamese (Vietnam)' },
    { code: 'nl-NL', name: 'Dutch (Netherlands)' },
    { code: 'sv-SE', name: 'Swedish (Sweden)' },
    { code: 'da-DK', name: 'Danish (Denmark)' },
    { code: 'no-NO', name: 'Norwegian (Norway)' },
    { code: 'fi-FI', name: 'Finnish (Finland)' },
    { code: 'pl-PL', name: 'Polish (Poland)' },
    { code: 'tr-TR', name: 'Turkish (Turkey)' }
  ]
}

// Auto-detect language based on browser settings
export const detectLanguage = (): string => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en-US'
  const supportedLangs = getSupportedLanguages().map(lang => lang.code)
  
  // Try exact match first
  if (supportedLangs.includes(browserLang)) {
    return browserLang
  }
  
  // Try language code without region
  const langCode = browserLang.split('-')[0]
  const fallback = supportedLangs.find(lang => lang.startsWith(langCode))
  
  return fallback || 'en-US'
}